import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, EnvelopeSimple, PaperPlaneTilt, Spinner } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { apiError } from "../lib/errors";

export default function LeaderPasswordResetRequest() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/leader/password-reset/request", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err, "Could not send reset email."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 sm:px-8 py-16 lg:py-24">
      <Link to="/auth/login" className="inline-flex items-center gap-1.5 text-sm text-[#8A948E] hover:text-[#1E3A2B] mb-4">
        <ArrowLeft size={14} weight="bold" /> Back to sign in
      </Link>
      <div className="anim-in delay-1">
        <div className="label-kicker" style={{ color: "#1E3A2B" }}>Reset Password</div>
        <h1 className="font-serif text-4xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
          Forgot your password?
        </h1>
        <p className="text-[#4A524C] mt-3 leading-relaxed">
          Enter the email you signed up with and we'll send you a link to set a new password.
        </p>
      </div>

      {sent ? (
        <div
          data-testid="reset-request-sent"
          className="mt-8 bg-[#F3F0EA] border border-[#E8E4DB] rounded-2xl px-5 py-6 anim-in delay-2"
        >
          <CheckCircle size={28} weight="duotone" className="text-[#1E3A2B]" />
          <h2 className="font-serif text-xl text-[#1E3A2B] mt-3">Check your email.</h2>
          <p className="text-sm text-[#4A524C] mt-2 leading-relaxed">
            If an account exists for <strong>{email}</strong>, you'll get a reset link in the next minute or so.
            The link expires after a short window for your security.
          </p>
          <Link
            to="/auth/login"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#1E3A2B]"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4 anim-in delay-2">
          <label className="block">
            <span className="label-kicker block mb-2">Email</span>
            <div className="relative">
              <EnvelopeSimple size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="reset-request-email"
                required
                autoFocus
                autoComplete="email"
                placeholder="you@company.com"
                className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-4 py-3.5 text-[#121815] placeholder-[#8A948E]"
              />
            </div>
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="reset-request-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1E3A2B] text-white px-5 py-3.5 rounded-xl font-medium hover:bg-[#2C4C3B] disabled:opacity-60"
          >
            {busy ? <Spinner size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} weight="fill" />}
            Email me a reset link
          </button>
        </form>
      )}
    </div>
  );
}
